import cloneDeep from 'lodash/cloneDeep';

export const TOP_NAV_COMPONENT_NAME = 'topNav';

export const MAX_PAGE_NAME_LENGTH = 10;
export const MAX_TITLE_LENGTH = 20;
export const MAX_QUICK_ENTRY_LABEL_LENGTH = 2;
export const MAX_QUICK_ENTRIES = 2;
export const MAX_TITLE_IMAGE_SIZE = 2 * 1024 * 1024;

export const QUICK_ENTRY_DEFAULT_ORDER = ['search', 'gold', 'store'];
export const DEFAULT_PAGE_NAME = '';

export function syncTopNavTitleText(config: any = {}) {
    if (config.titleEnabled !== false && config.titleMode !== 'image') {
        const pageName = (config.pageName || '').trim();
        if (pageName) {
            config.titleText = config.pageName;
        }
    }
    return config;
}

export function normalizeTopNavConfig(config: any = {}) {
    const nextConfig = { ...config };
    if (nextConfig.blurEnabled && !nextConfig.colorType) {
        nextConfig.colorType = 'blur';
    }
    if (!nextConfig.colorType) {
        nextConfig.colorType = 'solid';
    }
    if (typeof nextConfig.quickEntryEnabled !== 'boolean') {
        nextConfig.quickEntryEnabled =
            Array.isArray(nextConfig.quickEntries) && nextConfig.quickEntries.length > 0;
    }
    if (!nextConfig.quickEntryOrder) {
        nextConfig.quickEntryOrder = [...QUICK_ENTRY_DEFAULT_ORDER];
    }
    if (!nextConfig.quickEntryLabels) {
        nextConfig.quickEntryLabels = {};
    }
    if (!nextConfig.searchAlign) {
        nextConfig.searchAlign = 'center';
    }
    if (!nextConfig.titleAlign) {
        nextConfig.titleAlign = 'center';
    }
    if (nextConfig.style === 'standard' && nextConfig.colorType === 'blur') {
        nextConfig.colorType = 'solid';
    }
    syncTopNavTitleText(nextConfig);
    return nextConfig;
}

export const DEFAULT_TOP_NAV_CONFIG = {
    pageName: DEFAULT_PAGE_NAME,
    style: 'standard',
    titleEnabled: true,
    titleMode: 'text',
    titleText: DEFAULT_PAGE_NAME,
    titleImage: '',
    titleAlign: 'center',
    searchEnabled: false,
    searchAlign: 'center',
    quickEntryEnabled: false,
    quickEntries: [] as string[],
    quickEntryLabels: {} as Record<string, string>,
    quickEntryOrder: [...QUICK_ENTRY_DEFAULT_ORDER] as string[],
    colorType: 'solid',
    bgColor: '#ffffff',
    textColor: 'black',
};

export function getQuickEntryEnabled(config: any = {}) {
    if (typeof config.quickEntryEnabled === 'boolean') {
        return config.quickEntryEnabled;
    }
    // 兼容旧数据：旧配置只有 quickEntries 数组
    return Array.isArray(config.quickEntries) && config.quickEntries.length > 0;
}

export function getQuickEntryOrder(config: any = {}) {
    if (Array.isArray(config.quickEntryOrder) && config.quickEntryOrder.length > 0) {
        return config.quickEntryOrder;
    }
    return [...QUICK_ENTRY_DEFAULT_ORDER];
}

export function getEnabledFunctionCount(config: any = {}) {
    let count = 0;
    if (config.titleEnabled) count++;
    if (config.searchEnabled) count++;
    if (getQuickEntryEnabled(config)) count++;
    return count;
}

export function validateTopNavConfig(config: any = {}) {
    const pageName = (config.pageName || '').trim();
    if (!pageName) {
        return { valid: false, message: '页面名称不能为空' };
    }
    if (pageName.length > MAX_PAGE_NAME_LENGTH) {
        return { valid: false, message: `页面名称最多 ${MAX_PAGE_NAME_LENGTH} 个字` };
    }
    if (getEnabledFunctionCount(config) > 2) {
        return {
            valid: false,
            message: '「标题、搜索、快捷入口」最多只能同时开启2个',
        };
    }
    const quickEntryEnabled = getQuickEntryEnabled(config);
    if (
        quickEntryEnabled &&
        config.quickEntries &&
        config.quickEntries.length > MAX_QUICK_ENTRIES
    ) {
        return { valid: false, message: `最多可同时配置 ${MAX_QUICK_ENTRIES} 个快捷入口` };
    }
    if (config.titleEnabled) {
        if (config.titleMode === 'image') {
            if (!config.titleImage) {
                return { valid: false, message: '请上传标题图片' };
            }
        } else if (!(config.titleText || '').trim()) {
            return { valid: false, message: '请填写标题文字' };
        }
    }
    return { valid: true, message: '' };
}

export function findTopNavIndex(profile: any) {
    if (!profile || !profile.root || !profile.root.items) {
        return -1;
    }
    return profile.root.items.findIndex((item: any) => {
        const component = profile.components[item.component];
        return component && component.component === TOP_NAV_COMPONENT_NAME;
    });
}

export function hasTopNav(profile: any) {
    return findTopNavIndex(profile) >= 0;
}

function generateComponentId(name: string) {
    return `${name}-${parseInt(String(Math.random() * 1e8), 10).toString(16)}`;
}

export function createTopNavComponent(pageName = '') {
    const resolvedPageName = pageName || DEFAULT_PAGE_NAME;
    const id = generateComponentId(TOP_NAV_COMPONENT_NAME);
    return {
        id,
        config: {
            component: TOP_NAV_COMPONENT_NAME,
            ...DEFAULT_TOP_NAV_CONFIG,
            pageName: resolvedPageName,
            titleText: resolvedPageName,
        },
    };
}

export function ensureHomeTopNav(profile: any, pageName = '') {
    if (!profile || profile.metainfo?.type !== 'home') {
        return profile;
    }

    const nextProfile = cloneDeep(profile);
    if (!nextProfile.root) {
        nextProfile.root = { items: [] };
    }
    if (!nextProfile.root.items) {
        nextProfile.root.items = [];
    }
    if (!nextProfile.components) {
        nextProfile.components = {};
    }

    const resolvedPageName =
        pageName || nextProfile.metainfo?.title || DEFAULT_TOP_NAV_CONFIG.pageName;
    const topNavIndex = findTopNavIndex(nextProfile);

    if (topNavIndex >= 0) {
        const topNavItem = nextProfile.root.items.splice(topNavIndex, 1)[0];
        nextProfile.root.items.unshift(topNavItem);
        let topNavConfig = nextProfile.components[topNavItem.component];
        if (topNavConfig) {
            if (!topNavConfig.pageName) {
                topNavConfig.pageName = resolvedPageName;
            }
            topNavConfig = normalizeTopNavConfig(topNavConfig);
            nextProfile.components[topNavItem.component] = topNavConfig;
        }
        return nextProfile;
    }

    const created = createTopNavComponent(resolvedPageName);
    nextProfile.components[created.id] = created.config;
    nextProfile.root.items.unshift({ component: created.id });
    return nextProfile;
}

export function isTopNavWidget(profile: any, index: number) {
    const item = profile?.root?.items?.[index];
    if (!item) return false;
    const component = profile.components[item.component];
    return component && component.component === TOP_NAV_COMPONENT_NAME;
}
